import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../../services/api';

interface WeatherForecast {
  date: string;
  temp_min: number;
  temp_max: number;
  avg_rain_probability: number;
  main_condition: string;
  will_rain: boolean;
}

interface WeatherPlanningProps {
  selectedDate: string;
}

const WeatherPlanning: React.FC<WeatherPlanningProps> = ({ selectedDate }) => {
  const [forecasts, setForecasts] = useState<WeatherForecast[]>([]);
  const [location, setLocation] = useState<{ name: string; country: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [geoError, setGeoError] = useState<string | null>(null);

  const fetchWeather = useCallback(async (lat: number, lon: number) => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getWeather(lat, lon);
      if (response.success) {
        setForecasts(response.daily || []);
        setLocation(response.location);
      }
    } catch (err: any) {
      console.error('Failed to fetch weather:', err);
      setError(err.response?.data?.error || 'Failed to fetch weather data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Try to get user's location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          fetchWeather(position.coords.latitude, position.coords.longitude);
        },
        (err) => {
          console.error('Geolocation error:', err);
          setGeoError('Location access denied. Please enable location or enter a city.');
          setLoading(false);
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      setGeoError('Geolocation is not supported by your browser');
      setLoading(false);
    }
  }, [fetchWeather]);

  const getWeatherIcon = (condition: string) => {
    switch (condition?.toLowerCase()) {
      case 'clear': return '☀️';
      case 'clouds': return '☁️';
      case 'rain': return '🌧️';
      case 'drizzle': return '🌦️';
      case 'thunderstorm': return '⛈️';
      case 'snow': return '❄️';
      case 'mist':
      case 'fog': return '🌫️';
      default: return '🌤️';
    }
  };

  const getConditionColor = (condition: string) => {
    switch (condition?.toLowerCase()) {
      case 'clear': return '#fbbf24';
      case 'clouds': return '#9ca3af';
      case 'rain':
      case 'drizzle':
      case 'thunderstorm': return '#3b82f6';
      case 'snow': return '#bfdbfe';
      default: return '#6b7280';
    }
  };

  const isSelectedDate = (dateStr: string) => dateStr === selectedDate;

  return (
    <div style={{ padding: '24px' }} data-testid="weather-planning">
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#1f2937' }}>Weather-Aware Planning</h2>
        <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '0.9rem' }}>
          Plan outdoor activities based on weather forecasts
        </p>
        {location && (
          <p style={{ margin: '8px 0 0 0', color: '#3b82f6', fontSize: '1rem', fontWeight: 500 }}>
            {location.name}, {location.country}
          </p>
        )}
      </div>

      {/* Loading/Error States */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <p style={{ color: '#6b7280' }}>Fetching weather data...</p>
        </div>
      )}

      {geoError && !loading && (
        <div style={{ padding: '20px', backgroundColor: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '8px', marginBottom: '20px' }}>
          <p style={{ margin: '0 0 12px 0', color: '#92400e' }}>{geoError}</p>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button
              onClick={() => {
                setGeoError(null);
                setLoading(true);
                if (navigator.geolocation) {
                  navigator.geolocation.getCurrentPosition(
                    (position) => fetchWeather(position.coords.latitude, position.coords.longitude),
                    () => { setGeoError('Location still denied. Try the demo location below.'); setLoading(false); },
                    { enableHighAccuracy: true, timeout: 10000 }
                  );
                }
              }}
              style={{ padding: '8px 16px', backgroundColor: '#f59e0b', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 500 }}
            >
              Try Again
            </button>
            <button
              onClick={() => { setGeoError(null); fetchWeather(40.7128, -74.0060); }}
              style={{ padding: '8px 16px', backgroundColor: 'white', color: '#92400e', border: '1px solid #f59e0b', borderRadius: '6px', cursor: 'pointer', fontWeight: 500 }}
            >
              Use Demo (New York)
            </button>
          </div>
        </div>
      )}

      {error && (
        <div style={{ padding: '20px', backgroundColor: '#fee2e2', border: '1px solid #ef4444', borderRadius: '8px', marginBottom: '20px' }}>
          <p style={{ margin: 0, color: '#991b1b' }}>{error}</p>
        </div>
      )}

      {/* Forecast Cards */}
      {!loading && forecasts.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '16px' }}>
          {forecasts.map((day) => (
            <div
              key={day.date}
              style={{
                padding: '20px',
                backgroundColor: isSelectedDate(day.date) ? '#eff6ff' : 'white',
                borderRadius: '12px',
                border: isSelectedDate(day.date) ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                textAlign: 'center',
                transition: 'all 0.2s'
              }}
            >
              <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: 600, color: '#6b7280' }}>
                {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
              </p>
              
              <div style={{ margin: '16px 0', fontSize: '3rem' }}>
                {getWeatherIcon(day.main_condition)}
              </div>
              
              <p style={{ margin: '0 0 8px 0', fontSize: '0.9rem', fontWeight: 500, color: getConditionColor(day.main_condition) }}>
                {day.main_condition}
              </p>
              
              <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginBottom: '8px' }}>
                <span style={{ color: '#ef4444', fontWeight: 600 }}>{day.temp_max}°</span>
                <span style={{ color: '#3b82f6', fontWeight: 600 }}>{day.temp_min}°</span>
              </div>
              
              {day.avg_rain_probability > 0 && (
                <p style={{
                  margin: 0,
                  fontSize: '0.8rem',
                  color: day.avg_rain_probability > 50 ? '#3b82f6' : '#9ca3af'
                }}>
                  🌧️ {day.avg_rain_probability}%
                </p>
              )}
              
              {day.will_rain && (
                <div style={{
                  marginTop: '12px',
                  padding: '6px 12px',
                  backgroundColor: '#dbeafe',
                  borderRadius: '20px',
                  fontSize: '0.75rem',
                  color: '#1d4ed8'
                }}>
                  Rain expected
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Tips Section */}
      <div style={{ marginTop: '24px', padding: '20px', backgroundColor: '#f0f9ff', borderRadius: '12px', border: '1px solid #bae6fd' }}>
        <h3 style={{ margin: '0 0 12px 0', fontSize: '1rem', fontWeight: 600, color: '#0369a1' }}>Weather Planning Tips</h3>
        <ul style={{ margin: 0, paddingLeft: '20px', color: '#0c4a6e', fontSize: '0.9rem' }}>
          <li style={{ marginBottom: '8px' }}>Mark tasks as &quot;outdoor&quot; in the planner to get weather alerts</li>
          <li style={{ marginBottom: '8px' }}>Rain probability over 50% suggests rescheduling outdoor activities</li>
          <li style={{ marginBottom: '8px' }}>Weather forecasts update every few hours for accuracy</li>
          <li>High temperature days (35°C+) - plan outdoor work for early morning or evening</li>
        </ul>
      </div>

      {/* Selected Day Details */}
      {forecasts.length > 0 && (
        <div style={{ marginTop: '24px' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '1.1rem', fontWeight: 600, color: '#374151' }}>
            Forecast for {new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
          </h3>
          {forecasts.find(f => f.date === selectedDate) ? (
            (() => {
              const dayForecast = forecasts.find(f => f.date === selectedDate)!;
              return (
                <div style={{ padding: '24px', backgroundColor: '#f9fafb', borderRadius: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '4rem' }}>{getWeatherIcon(dayForecast.main_condition)}</span>
                    <div>
                      <p style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#1f2937' }}>{dayForecast.main_condition}</p>
                      <p style={{ margin: '4px 0 0 0', fontSize: '1.1rem', color: '#6b7280' }}>
                        High: <strong style={{ color: '#ef4444' }}>{dayForecast.temp_max}°C</strong> • 
                        Low: <strong style={{ color: '#3b82f6' }}>{dayForecast.temp_min}°C</strong>
                      </p>
                      <p style={{ margin: '4px 0 0 0', fontSize: '0.9rem', color: '#9ca3af' }}>
                        Rain probability: {dayForecast.avg_rain_probability}%
                      </p>
                    </div>
                  </div>
                  
                  {dayForecast.will_rain && (
                    <div style={{ marginTop: '16px', padding: '12px 16px', backgroundColor: '#fef3c7', borderRadius: '8px', border: '1px solid #fbbf24' }}>
                      <p style={{ margin: 0, color: '#92400e', fontSize: '0.9rem' }}>
                        <strong>Weather Alert:</strong> Rain is expected today. Consider rescheduling outdoor tasks or bringing an umbrella!
                      </p>
                    </div>
                  )}
                </div>
              );
            })()
          ) : (
            <p style={{ color: '#6b7280' }}>No forecast available for this date. Weather data is typically available for the next 5 days.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default WeatherPlanning;
